import { PlusOutlined } from '@ant-design/icons'
import { Button, Space } from 'antd'
import { useState } from 'react'

import {
  useCountryExclusionChange,
  useCountryExclusions,
  useOfferExclusionChange,
  useOfferExclusions,
} from '../hooks/useGpuPolicies'
import { ReasonModal } from './ReasonModal'
import { RulesTable } from './RulesTable'

interface Props {
  provider: string
  canWrite: boolean
}

export function CountryExclusionsTab({ provider, canWrite }: Props) {
  const { data, isLoading, error } = useCountryExclusions(provider)
  const { exclude, allow } = useCountryExclusionChange(provider)
  const [open, setOpen] = useState(false)

  return (
    <Space direction="vertical" style={{ width: '100%' }}>
      {canWrite && (
        <Button icon={<PlusOutlined />} onClick={() => setOpen(true)}>
          Exclude a country
        </Button>
      )}
      <RulesTable
        rows={data?.map((c) => ({ ...c, key: c.country_code }))}
        loading={isLoading}
        error={error}
        keyTitle="Country"
        emptyText="No excluded countries"
        canWrite={canWrite}
        removeAction="Return to sale"
        onRemove={(country) => allow.mutateAsync({ key: country })}
      />
      <ReasonModal
        open={open}
        title="Exclude a country from sales"
        action="Exclude"
        target={{
          label: 'Country (ISO 3166-1 alpha-2)',
          placeholder: 'DE',
          normalize: (v: string) => v.trim().toUpperCase(),
          rules: [
            { required: true, message: 'Enter a country code' },
            { pattern: /^[A-Z]{2}$/, message: 'Two letters, e.g. DE' },
          ],
        }}
        maxReason={1000}
        existing={data?.map((c) => c.country_code)}
        loading={exclude.isPending}
        onSubmit={(key, reason) => exclude.mutateAsync({ key, reason })}
        onClose={() => setOpen(false)}
      />
    </Space>
  )
}

export function OfferExclusionsTab({ provider, canWrite }: Props) {
  const { data, isLoading, error } = useOfferExclusions(provider)
  const { allow } = useOfferExclusionChange(provider)

  return (
    <RulesTable
      rows={data?.map((o) => ({ ...o, key: o.provider_offer_id }))}
      loading={isLoading}
      error={error}
      keyTitle="Provider offer id"
      emptyText="No offers taken off sale"
      canWrite={canWrite}
      removeAction="Return to sale"
      onRemove={(id) => allow.mutateAsync({ key: id })}
    />
  )
}
